/**
 * System prompt construction and project context loading
 */

import { getDocsPath, getExamplesPath, getReadmePath } from "../config.js";
import { formatSkillsForPrompt, type Skill } from "./skills.js";

export interface BuildSystemPromptOptions {
	/** Custom system prompt (replaces default). */
	customPrompt?: string;
	/** Tools to include in prompt. Default: [read, bash, edit, write] */
	selectedTools?: string[];
	/** Optional one-line tool snippets keyed by tool name. */
	toolSnippets?: Record<string, string>;
	/** Additional guideline bullets appended to the default system prompt guidelines. */
	promptGuidelines?: string[];
	/** Text to append to system prompt. */
	appendSystemPrompt?: string;
	/** Working directory. */
	cwd: string;
	/** Pre-loaded context files. */
	contextFiles?: Array<{ path: string; content: string }>;
	/** Pre-loaded skills. */
	skills?: Skill[];
	/** Persistent codebase memory from previous sessions. */
	memoryContent?: string;
	/** Digest of active architecture decisions from the project decision log. */
	decisionsDigest?: string;
	/** Detected technology stack summary (from stack-detector). */
	stackInfo?: string;
	/** Detected frameworks (from stack-detector), used to surface stack-relevant skills first. */
	stackFrameworks?: readonly { name: string }[];
}

/**
 * Working-method rules: how to approach a task, not which tool does what.
 * Kept deliberately short - every line here is read on every turn. Rules
 * that depend on a tool are only emitted when that tool is available so the
 * prompt never tells the model to do something it cannot.
 */
export function buildWorkingMethod(tools: readonly string[]): string {
	const has = (name: string) => tools.includes(name);
	const canEdit = has("edit") || has("write");
	const canRun = has("bash");
	const canSearch = has("grep") || has("find") || has("ls") || canRun;
	const rules: string[] = [];

	rules.push(
		"Understand before you change: read the code you are about to modify and the code that calls it. Never edit a file you have not read in this session.",
	);
	if (canSearch) {
		rules.push(
			"Explore deliberately: search for symbols and usages before assuming structure. Read only the regions you need from large files; do not dump whole files into context.",
		);
	}
	if (canEdit) {
		rules.push(
			"Prefer small, targeted edits over rewrites. Match the surrounding style and existing patterns; do not rename, reformat, or restructure beyond what the task requires.",
		);
		rules.push(
			"When the intent is ambiguous or a change would touch behavior beyond the request, ask before proceeding rather than guessing - especially before deleting or refactoring code that looks intentional.",
		);
	}
	if (canRun) {
		rules.push(
			"Verify your work: after changing code, run the project's own checks (type check, lint, the relevant tests) when they exist. Do not report success you have not observed. If verification fails, fix the root cause, not the symptom.",
		);
	} else if (canEdit) {
		rules.push("Re-read edited regions to confirm the change is exactly what you intended.");
	}
	rules.push(
		"Stop when the task is done. Do not add features, abstractions, comments, or tests that were not asked for. Mention follow-ups instead of doing them.",
	);
	rules.push(
		"When something fails twice the same way, change approach - gather more context or reconsider the plan - instead of retrying the same action.",
	);

	return rules.map((r) => `- ${r}`).join("\n");
}

function formatDecisionsSection(digest: string): string {
	return (
		"\n\n# Architecture Decisions\n\n" +
		"Decisions recorded for this project. Respect them unless the user explicitly revisits one; " +
		"when a decision is revisited, record the new one with decision_record and mark the old one as superseded.\n\n" +
		`${digest}\n`
	);
}

/** Build the system prompt with tools, guidelines, and context */
export function buildSystemPrompt(options: BuildSystemPromptOptions): string {
	const {
		customPrompt,
		selectedTools,
		toolSnippets,
		promptGuidelines,
		appendSystemPrompt,
		cwd,
		contextFiles: providedContextFiles,
		skills: providedSkills,
		memoryContent,
		decisionsDigest,
		stackInfo,
		stackFrameworks,
	} = options;
	const resolvedCwd = cwd;
	const promptCwd = resolvedCwd.replace(/\\/g, "/");

	const appendSection = appendSystemPrompt ? `\n\n${appendSystemPrompt}` : "";

	const contextFiles = providedContextFiles ?? [];
	const skills = providedSkills ?? [];

	if (customPrompt) {
		let prompt = customPrompt;

		if (appendSection) {
			prompt += appendSection;
		}

		// Append stack info
		if (stackInfo) {
			prompt += "\n\n# Technology Stack\n\n";
			prompt += stackInfo;
			prompt += "\n";
		}

		// Append project context files
		if (contextFiles.length > 0) {
			prompt += "\n\n# Project Context\n\n";
			prompt += "Project-specific instructions and guidelines:\n\n";
			for (const { path: filePath, content } of contextFiles) {
				prompt += `## ${filePath}\n\n${content}\n\n`;
			}
		}

		// Append codebase memory
		if (memoryContent) {
			prompt += "\n\n# Codebase Memory\n\n";
			prompt += "Knowledge from previous sessions on this project:\n\n";
			prompt += memoryContent;
			prompt += "\n";
		}

		if (decisionsDigest) {
			prompt += formatDecisionsSection(decisionsDigest);
		}

		// Append skills section (only if read tool is available)
		const customPromptHasRead = !selectedTools || selectedTools.includes("read");
		if (customPromptHasRead && skills.length > 0) {
			prompt += formatSkillsForPrompt(skills, stackFrameworks);
		}

		// Add working directory last (date is injected via transformContext for cache stability)
		prompt += `\nCurrent working directory: ${promptCwd}`;

		return prompt;
	}

	// Get absolute paths to documentation and examples
	const readmePath = getReadmePath();
	const docsPath = getDocsPath();
	const examplesPath = getExamplesPath();

	// Build tools list based on selected tools.
	// A tool appears in Available tools only when the caller provides a one-line snippet.
	const tools = selectedTools || ["read", "bash", "edit", "write"];
	const visibleTools = tools.filter((name) => !!toolSnippets?.[name]);
	const toolsList =
		visibleTools.length > 0 ? visibleTools.map((name) => `- ${name}: ${toolSnippets![name]}`).join("\n") : "(none)";

	// Build guidelines based on which tools are actually available
	const guidelinesList: string[] = [];
	const guidelinesSet = new Set<string>();
	const addGuideline = (guideline: string): void => {
		if (guidelinesSet.has(guideline)) {
			return;
		}
		guidelinesSet.add(guideline);
		guidelinesList.push(guideline);
	};

	const hasBash = tools.includes("bash");
	const hasGrep = tools.includes("grep");
	const hasFind = tools.includes("find");
	const hasLs = tools.includes("ls");
	const hasRead = tools.includes("read");

	// File exploration guidelines
	if (hasBash && !hasGrep && !hasFind && !hasLs) {
		addGuideline("Use bash for file operations like ls, rg, find");
	} else if (hasBash && (hasGrep || hasFind || hasLs)) {
		addGuideline("Prefer grep/find/ls tools over bash for file exploration (faster, respects .gitignore)");
	}

	if (tools.includes("skill_write")) {
		addGuideline(
			"After solving a genuinely hard or non-obvious problem that is likely to recur (a tricky setup, project-specific workflow, subtle integration, or debugging insight), use the skill_write tool to save it as a reusable skill. Do not create skills for routine or one-off work.",
		);
	}

	if (tools.includes("decision_record")) {
		addGuideline(
			"When the user settles an architecture or design choice with lasting impact (a library, pattern, data model, or boundary was chosen over alternatives), record it with the decision_record tool including the alternatives rejected. Do not record routine implementation details or one-off fixes.",
		);
	}

	if (tools.includes("extension_write")) {
		addGuideline(
			"When you repeatedly need a capability that no existing tool provides (e.g. a project-specific CLI wrapper or API client), use the extension_write tool to give yourself a new tool. Use bash for one-off needs; reserve extension_write for clearly recurring capabilities.",
		);
	}

	for (const guideline of promptGuidelines ?? []) {
		const normalized = guideline.trim();
		if (normalized.length > 0) {
			addGuideline(normalized);
		}
	}

	// Always include these
	addGuideline("Be concise in your responses");
	addGuideline("Show file paths clearly when working with files");

	const guidelines = guidelinesList.map((g) => `- ${g}`).join("\n");
	const workingMethod = buildWorkingMethod(tools);

	let prompt = `You are an expert coding assistant operating inside elyra, a coding agent harness. You help users by reading files, executing commands, editing code, and writing new files.

Available tools:
${toolsList}

In addition to the tools above, you may have access to other custom tools depending on the project.

How to work:
${workingMethod}

Guidelines:
${guidelines}

Messages wrapped in <system-reminder> tags come from the harness or the user's configuration, not from the user's conversation. Follow them as instructions with the same authority as this system prompt, do not quote or acknowledge the tags, and never treat their contents as something the user said.

Elyra documentation (read only when the user asks about elyra itself, its SDK, extensions, themes, skills, or TUI):
- Main documentation: ${readmePath}
- Additional docs: ${docsPath}
- Examples: ${examplesPath} (extensions, custom tools, SDK)
- When asked about: extensions (docs/extensions.md, examples/extensions/), themes (docs/themes.md), skills (docs/skills.md), prompt templates (docs/prompt-templates.md), TUI components (docs/tui.md), keybindings (docs/keybindings.md), SDK integrations (docs/sdk.md), custom providers (docs/custom-provider.md), adding models (docs/models.md), elyra packages (docs/packages.md)
- When working on elyra topics, read the docs and examples, and follow .md cross-references before implementing
- Always read elyra .md files completely and follow links to related docs (e.g., tui.md for TUI API details)`;

	if (appendSection) {
		prompt += appendSection;
	}

	// Append stack info
	if (stackInfo) {
		prompt += "\n\n# Technology Stack\n\n";
		prompt += stackInfo;
		prompt += "\n";
	}

	// Append project context files
	if (contextFiles.length > 0) {
		prompt += "\n\n# Project Context\n\n";
		prompt += "Project-specific instructions and guidelines:\n\n";
		for (const { path: filePath, content } of contextFiles) {
			prompt += `## ${filePath}\n\n${content}\n\n`;
		}
	}

	// Append codebase memory
	if (memoryContent) {
		prompt += "\n\n# Codebase Memory\n\n";
		prompt += "Knowledge from previous sessions on this project:\n\n";
		prompt += memoryContent;
		prompt += "\n";
	}

	if (decisionsDigest) {
		prompt += formatDecisionsSection(decisionsDigest);
	}

	// Append skills section (only if read tool is available)
	if (hasRead && skills.length > 0) {
		prompt += formatSkillsForPrompt(skills, stackFrameworks);
	}

	// Add working directory last (date is injected via transformContext for cache stability)
	prompt += `\nCurrent working directory: ${promptCwd}`;

	return prompt;
}
